/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { signOut } from 'next-auth/react';
import clsx from 'clsx';
import Icons from '@/const/icons';
import { Icon } from '@/components/common';
import { usePathname, useSearchParams } from 'next/navigation';
import AppRoutes from '@/const/routes';
import { RoleType } from '@/interfaces';
import { SurveyModal } from '@/components/modals/SurveyModal';
import { useRouter } from 'next/router';
import { feedbackService } from '@/api';
import { useQuery } from '@tanstack/react-query';
import { getLocalStorage } from '@/utils/localStorage';
import { signOutLawyer } from '@/lib';
import { ActiveLine } from './ActiveLine';

const UserMenu = ({
	role,
	session,
	setShowSidebar,
}: {
	role: RoleType;
	session: any;
	setShowSidebar?: (value: boolean) => void;
}): JSX.Element => {
	const pathname = usePathname();
	const router = useRouter();
	const { action } = router.query;
	const searchParams = useSearchParams();
	const [isOpenFeedBackModal, setIsOpenFeedBackModal] = useState(false);
	const [userId, setUserId] = useState<string | null>(null);

	useEffect(() => {
		const storedUserId = getLocalStorage('user_id');
		if (storedUserId) {
			setUserId(storedUserId);
		}
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(searchParams.toString());

		if (isOpenFeedBackModal) {
			params.set('feedback', 'true');
		} else {
			params.delete('feedback');
		}

		const queryString = params.toString();
		const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

		if (router.asPath !== newUrl) {
			router.replace(newUrl);
		}
	}, [isOpenFeedBackModal]);

	useEffect(() => {
		if (action && action === 'feedback') {
			setIsOpenFeedBackModal(true);
		}
	}, [action]);

	useEffect(() => {
		pathname &&
			setTimeout(() => {
				setShowSidebar?.(false);
			}, 500);
	}, [pathname]);

	const { data: feedback, refetch: refetchFeedback } = useQuery({
		queryKey: ['feedback', userId],
		queryFn: () => feedbackService.getByUserId(userId || ''),
		enabled: !!userId,
		retry: (failureCount, error) =>
			(error as any)?.status === 404 ? false : failureCount < 1,
		staleTime: 5 * 60 * 1000,
	});

	useEffect(() => {
		if (isOpenFeedBackModal === false) {
			refetchFeedback();
		}
	}, [isOpenFeedBackModal, refetchFeedback]);

	return (
		<div className="flex lg:block bg-white rounded-lg h-full gap-x-10 border-t border-grey-200 lg:border-none">
			<hr />
			{role === 'client' && (
				<>
					<NavigationItem
						label="Feedback"
						icon={Icons.document}
						href={'#'}
						onClick={() => setIsOpenFeedBackModal(true)}
						isOpenFeedBackModal={isOpenFeedBackModal}
						className={feedback?.data > 0 ? 'hidden' : 'block'}
					/>
					<NavigationItem
						label="Customer Support"
						icon={Icons.question}
						href={AppRoutes.CLIENTAPP_CUSTOMER_SUPPORT}
						isOpenFeedBackModal={isOpenFeedBackModal}
					/>
				</>
			)}
			<NavigationItem
				label="Settings"
				icon={Icons.settings}
				href={
					role === 'client'
						? AppRoutes.CLIENTAPP_SETTINGS
						: AppRoutes.LAWYERAPP_SETTINGS
				}
				isOpenFeedBackModal={isOpenFeedBackModal}
			/>

			{role === 'lawyer' && (
				<NavigationItem
					label="Admin"
					icon={Icons.profileUsers}
					href={AppRoutes.LAWYERAPP_ADMIN_PANEL}
					isOpenFeedBackModal={isOpenFeedBackModal}
				/>
			)}
			<button
				title="Logout"
				className="flex lg:w-full items-center justify-center py-5"
				onClick={async () => {
					if (role === 'lawyer') {
						signOutLawyer();
					} else {
						await signOut({
							redirect: true,
							callbackUrl: AppRoutes.CLIENTAPP_AUTH_SIGN_IN,
						});
					}
				}}
			>
				<Icon src={Icons.logout} className="!size-5 text-opacity-black" />
			</button>

			{pathname.includes('/clientapp') && session && (
				<SurveyModal
					user={session.data?.user}
					isOpen={isOpenFeedBackModal}
					setIsOpen={setIsOpenFeedBackModal}
				/>
			)}
		</div>
	);
};

interface NavigationItemProps {
	label: string;
	isOpenFeedBackModal: boolean;
	href: string;
	icon: string;
	className?: string;
	onClick?: () => void;
}
const NavigationItem: React.FC<NavigationItemProps> = ({
	icon,
	label,
	href,
	isOpenFeedBackModal,
	className,
	onClick,
}) => {
	const pathname = usePathname();
	const isCurrentPath = pathname.includes(href);

	const content = (
		<>
			{isCurrentPath && !isOpenFeedBackModal && <ActiveLine />}
			<Icon
				src={icon}
				className={clsx('!size-5', {
					'!text-primary': isCurrentPath && !isOpenFeedBackModal,
					'text-opacity-black': !isCurrentPath,
				})}
			/>
		</>
	);

	return onClick ? (
		<button
			className={clsx(
				'flex lg:w-full items-center justify-center gap-2 py-5',
				className
			)}
			onClick={onClick}
			title={label}
		>
			{content}
		</button>
	) : (
		<Link
			href={href}
			className={clsx(
				'flex items-center lg:w-full justify-center gap-2 py-5',
				className
			)}
			title={label}
		>
			{content}
		</Link>
	);
};

export default UserMenu;
